import {produce} from 'immer';
import React, {useCallback} from 'react';

import {useDispatch} from '../../state/hooks';
import * as ViewHooks from '../../state/views/hooks';
import * as SortActions from '../../state/views/sort/actions';
import type * as SortTypes from '../../state/views/sort/types';
import type * as QueryTypes from '../../util/queryTypes';
import {type Config as TableSettings} from '../../util/runfeed';
import * as Run from '../../util/runs';
import {NamedProjectFieldSelector} from '../ProjectFieldSelector';
import {
  WBTableActionSort,
  WBTableActionSortButton,
  WBTableActionSortPicker,
} from './WBTable/WBTableActionsSort';

interface RunsSortPickerProps {
  entityName: string;
  projectName: string;
  tableSettings: TableSettings;
  sortRef: SortTypes.Ref;
  onSortChanged(): void;
}

const RunsSortPicker = (props: RunsSortPickerProps) => {
  const {entityName, projectName, tableSettings, sortRef, onSortChanged} =
    props;

  const defaultKeys = [
    'run:displayName',
    'run:state',
    'run:createdAt',
    'run:updatedAt',
    'run:duration',
    'run:sweep',
    'run:host',
  ];

  const sort = ViewHooks.usePart(props.sortRef);
  const dispatch = useDispatch();
  const setSort = useCallback(
    (newSort: QueryTypes.Sort) => {
      dispatch(SortActions.set(sortRef, newSort));
      onSortChanged();
    },
    [dispatch, sortRef, onSortChanged]
  );

  const filterBy = useCallback((key: string) => {
    // Do not allow sorting on a key with a comma in it.
    // This will cause mysql to error.
    return !key.includes(',');
  }, []);

  return (
    <WBTableActionSortPicker
      sort={sort}
      setSort={setSort}
      makeDropdown={sortKeyIdx => (
        <NamedProjectFieldSelector
          data-test="sort-dropdown"
          className="sort"
          entityName={entityName}
          projectName={projectName}
          tableSettings={tableSettings}
          defaultKeys={defaultKeys}
          multi={false}
          selection
          searchByKeyAndText
          value={Run.keyToString(sort.keys[sortKeyIdx].key)}
          setValue={value =>
            setSort(
              produce(sort, draft => {
                draft.keys[sortKeyIdx].key = Run.keyFromString(value)!;
              })
            )
          }
          filterBy={filterBy}
        />
      )}
    />
  );
};

type RunsSortTableActionProps = RunsSortPickerProps & {
  compact?: boolean;
  open: boolean;
  onOpen(): void;
  onClose(): void;
};

export const RunsSortTableAction = (props: RunsSortTableActionProps) => {
  const sort = ViewHooks.usePart(props.sortRef);

  return (
    <WBTableActionSort
      open={props.open}
      onOpen={props.onOpen}
      onClose={props.onClose}
      trigger={open => (
        <WBTableActionSortButton
          onPress={() => {
            if (props.open) {
              props.onClose();
            } else {
              props.onOpen();
            }
          }}
          compact={props.compact}
          sortOpen={open}
          sort={sort}
        />
      )}
      content={
        <RunsSortPicker
          entityName={props.entityName}
          projectName={props.projectName}
          tableSettings={props.tableSettings}
          sortRef={props.sortRef}
          onSortChanged={props.onSortChanged}
        />
      }
    />
  );
};
